import PropTypes from 'prop-types';
import React, {useEffect, useLayoutEffect, useMemo,Suspense} from 'react';
import ImageFactory from '../../adapters/cloudinary/ImageFactory';
import { findIndexByAnchorId } from '../../adapters/helpers/Anchor';
import dynamic from 'next/dynamic';
import Backdrop from '../Backdrop/Backdrop';
const MarkdownText = dynamic(() => import('../MarkdownText/MarkdownText'));
const RichText = dynamic(() => import('../RichText/RichText'));
import { SeriesSelectorConstants  } from '../../adapters/helpers/Constants';
import SeriesTabs from './SeriesTabs';
import BuySeries from './BuySeries';
import SeriesComparison from './SeriesComparison';
import Block from '../../adapters/helpers/Block';
const Disclaimer = dynamic(() => import('../Disclaimer/Disclaimer'));
import { stripHtml, smoothVerticalScroll } from '../../adapters/helpers/Utils';
import { mediaQueryHOC } from '../../adapters/helpers/Hooks';
import Loader from '../../adapters/helpers/Loader';
import Image from 'next/image';
import LazyLoad from 'react-lazy-load';
import DOMPurify from 'isomorphic-dompurify';
function Series(props) {
    const {document: doc, isLarge, isXLarge,extraAttributes} = props;
    const { entity: collection, query, slug: basePath} = extraAttributes;
    const selectedSeriesId = query?.series
    const seriesArray = collection.series;
    const selectedSeriesIndex = useMemo(
        () => findIndexByAnchorId(seriesArray, selectedSeriesId) || 0,
        [selectedSeriesId, seriesArray],
    );
    const selectedSeries = seriesArray?.[selectedSeriesIndex];
    const selectedSeriesProducts = selectedSeries?.fields?.products;

    useEffect(() => {
        if (typeof (window.dataLayer) !== 'undefined') {
            window.dataLayer.push({
                'event': 'VirtualPageview',
                'title': `${stripHtml(selectedSeries?.fields?.shortTitle)}`,
                'url': `${selectedSeries?.fields?.virtualPageViewUrl}`
            });
        }
    }, [selectedSeriesIndex]);

    const selectedSeriesRelatedBlocks = selectedSeries?.fields?.relatedBlocks|| null;

    return (
        <div className='ob-series'>
            <Suspense fallback={<Loader />}>
            <SeriesSelector
                document={doc}
                collection={collection}
                seriesArray={seriesArray}
                selectedSeries={selectedSeries}
                selectedSeriesId={selectedSeriesId}
                selectedSeriesIndex={selectedSeriesIndex}
                onClickCallback={props.onClickCallback}
                basePath={basePath}
                isLarge={isLarge}
                isXLarge={isXLarge}
            />
           </Suspense>
            {!!(selectedSeriesProducts && selectedSeriesProducts.length > 1) && (
                <SeriesComparison
                    document={doc}
                    products={selectedSeriesProducts}
                    selectedSeries={selectedSeries}
                    onClickCallback={props.onClickCallback}
                />
            )}

            {selectedSeriesRelatedBlocks &&
                selectedSeriesRelatedBlocks.map((block, index) => {
                    return new Block(block).renderBlockFromDocument({}, () => {}, {}, index);
                })
            }
        </div>
    );
}

Series.propTypes = {
    document: PropTypes.object,
    extraAttributes: PropTypes.object,
    selectedSeries: PropTypes.object,
    onClickCallback: PropTypes.func.isRequired,
    selectedSeriesId: PropTypes.string,
}

function SeriesSelector(props) {
    const {document: doc, collection, onClickCallback} = props;
    const {seriesArray, selectedSeries, selectedSeriesId, selectedSeriesIndex, basePath, isXLarge, isLarge} = props;

    const blockSubtitleLabel = doc.fields.blockSubtitleLabel;
    const optionsLabel = doc.fields.optionsLabel;
    const disclaimer = collection?.disclaimer;

    const featuredProduct = selectedSeries?.fields?.featuredProduct;

    const mobileBackgroundAsset = selectedSeries?.fields?.mobileBackgroundAsset;
    const selectedSeriesBackground = isXLarge && isLarge ? mobileBackgroundAsset : selectedSeries?.fields?.backgroundAsset;
    const selectedSeriesOptions = selectedSeries?.fields?.options;

    const selectedSeriesImage = selectedSeries?.fields?.mainAsset;
    const selectedSeriesMobileImage = selectedSeries?.fields?.mobileAsset || featuredProduct?.fields?.mobileAsset;
    const seriesHeading = selectedSeries?.fields?.seriesHeading ? selectedSeries?.fields?.seriesHeading : collection.shortTitle;
    const shortTitle = collection.shortTitle;
    
    const style = {
        backgroundColor: selectedSeries?.fields?.backgroundColor,
    };
    const contentSelectedSeriesImage = selectedSeries?.fields?.mainAsset?.fields?.asset?.fields?.file?.url;
    const contentSelectedSeriesMobileImage = selectedSeries?.fields?.mobileAsset?.fields?.asset?.fields?.file?.url || featuredProduct?.fields?.mobileAsset?.fields?.asset?.fields?.file?.url

    const scrollToFeatures = event => {
        const currentElemHref = event.currentTarget.getAttribute('href');
        if (currentElemHref.charAt(0) === '#') {
            event.preventDefault();
            const targetElem = document.querySelector(currentElemHref);
            if (targetElem) {
                smoothVerticalScroll(targetElem, 'top');
            }
        }
    };

    useLayoutEffect(() => {
        if (document) {
            const exploreMoreAnchorLink = document.querySelectorAll('.series-explore-all-features a');
            if (exploreMoreAnchorLink) {
                exploreMoreAnchorLink.forEach(anchorLink => {
                    anchorLink.addEventListener('click', scrollToFeatures);
                    return () => anchorLink.removeEventListener('click', scrollToFeatures);
                })
            }
        }
    });


    // Master Product id containing the reviews of all the product in his family
    const reviewAndRatingMasterProductId = SeriesSelectorConstants.master + featuredProduct?.fields?.reviewAndRatingFamilyId;

    return (
        <LazyLoad offset={50} unmountIfInvisible={true}>
        <Suspense fallback={<Loader />}>
        <Backdrop className={`ob-series-selector ${selectedSeries?.fields?.anchorId}-series ${doc?.fields?.classNames ? doc?.fields?.classNames : ''}`} style={style}>
            <Backdrop.Image image={selectedSeriesBackground} mobileImage={mobileBackgroundAsset} />

            <Backdrop.Content className='ob-series-selector__content'>
                <div className='ob-series-selector__top'>
                { selectedSeriesId  ? <h1 className='ob-series-selector__heading' dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(seriesHeading)}}></h1> : <h1 className='ob-series-selector__heading' dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(shortTitle)}}></h1>}
                    {disclaimer &&
                        <Disclaimer document={disclaimer} />
                    }
                </div>

                <div className='ob-series-selector__series'>
                    {!!blockSubtitleLabel && (
                        <h2 className='ob-series-selector__subtitle'>
                            {blockSubtitleLabel.fields.text}
                        </h2>
                    )}
                   <Suspense fallback={<Loader />}>
                    <SeriesTabs
                        items={seriesArray}
                        collection={collection}
                        selectedItem={selectedSeries}
                        selectedIndex={selectedSeriesIndex}
                        selectedSeriesId={selectedSeriesId}
                        basePath={basePath}
                    />
                    </Suspense>
                </div>

                {!!selectedSeries && (
                    <Suspense fallback={<Loader />}>
                    <div className='ob-series-selector__selected'>
                        <div className='ob-series-selector__selected-main'>
                            <div className='ob-series-selector__selected-image'>
                            {!!contentSelectedSeriesImage && (
                                    <div className='ob-series-selector__selected-image-wrap'>
                                        <picture className='ob-series-selector__selected-image-pic'>
                                            <source media={SeriesSelectorConstants.media} srcSet={ImageFactory.imageContentfullDpr(selectedSeriesImage, SeriesSelectorConstants.dpr)} />

                                            {!!contentSelectedSeriesMobileImage && (
                                                <source srcSet={ImageFactory.imageContentfullDpr(selectedSeriesMobileImage, SeriesSelectorConstants.dpr)} />
                                            )}

                            <Image className='ob-series-selector__selected-image-img'
                                                alt={selectedSeriesImage.fields.alternateText || SeriesSelectorConstants.empty}
                                                src={ImageFactory.imageContentfullSrc(contentSelectedSeriesImage, ContentSeriesSelectorConstants.dpr)}
                                                srcSet={ImageFactory.imageContentfullDpr(contentSelectedSeriesImage, ContentSeriesSelectorConstants.dpr)}
                                                width={350}
                                                height={parseInt(selectedSeries.fields.assetHeight) || 711}
                                                style={{ width: 'auto', maxWidth: '100%', height: 'auto', maxHeight: selectedSeries.fields.assetHeight ? `${selectedSeries.fields.assetHeight}px` : '711px' }}
                                                sizes='(max-width: 768px) 100vw, 350px'
                                            />
                                        </picture>
                                    </div>
                                )}
                            </div>

                            <div className='ob-series-selector__selected-gap' />

                            <div className='ob-series-selector__selected-info'>

                            <h2 className='ob-series-selector__selected-name' dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(selectedSeries.fields.title)}}>
                                </h2>

                                {featuredProduct?.fields?.reviewAndRatingFamilyId && (
                                    <div data-bv-show={SeriesSelectorConstants.ratingSummary} data-bv-product-id={reviewAndRatingMasterProductId}></div>
                                )}

                                <MarkdownText className='ob-series-selector__selected-description'
                                    value={selectedSeries.fields.description}
                                />

                                {(selectedSeries.fields.highlights && selectedSeries.fields.highlights.length > 0) && (
                                    <ul className='ob-series-selector__selected-highlights'>

                                        {selectedSeries.fields.highlights.map((serieHightlight, i) => {

                                            if (serieHightlight?.fields?.highlight && serieHightlight?.fields?.iconName) {
                                                return (
                                                    <li className='ob-series-selector__selected-highlights-item' key={i}>
                                                        <span className={`ob-series-selector__selected-highlights-icon ${serieHightlight.fields.iconName}`} />

                                                        <span className='ob-series-selector__selected-highlights-text'>
                                                            <RichText document={serieHightlight.fields.highlight} />
                                                        </span>
                                                    </li>
                                                )
                                            } else if (serieHightlight?.fields?.highlight) {
                                                return (
                                                    <li className='ob-series-selector__selected-highlights-item' key={i}>
                                                        <span className='ob-series-selector__selected-highlights-bullet'>•</span>
                                                        <span className='ob-series-selector__selected-highlights-text'>
                                                            <RichText document={serieHightlight.fields.highlight} />
                                                        </span>
                                                    </li>
                                                )
                                            }

                                        })}
                                    </ul>
                                )}

                                {!!(selectedSeriesOptions) && (
                                    <div className='ob-series-selector__selected-options'>
                                        {!!optionsLabel && (
                                            <h3 className='ob-series-selector__selected-subheading ob-series-selector__selected-subheading--options'>
                                                {optionsLabel.fields.text}
                                            </h3>
                                        )}

                                        <MarkdownText className='ob-series-selector__selected-options-text'
                                            value={selectedSeriesOptions}
                                        />
                                    </div>
                                )}

                                <BuySeries onClickCallback={onClickCallback} className='ob-series-selector__selected-info-buy'
                                    series={selectedSeries}
                                />
                            </div>
                        </div>

                        <BuySeries onClickCallback={onClickCallback} className='ob-series-selector__selected-buy'
                            series={selectedSeries}
                        />
                    </div>
                    </Suspense>
                )}
            </Backdrop.Content>
        </Backdrop>
        </Suspense>
        </LazyLoad>
    );
}
export default mediaQueryHOC(Series);
SeriesSelector.propTypes = {
    document: PropTypes.object,
    collection: PropTypes.any,
    seriesArray: PropTypes.any,
    selectedSeries: PropTypes.object,
    selectedSeriesId: PropTypes.string,
    selectedSeriesIndex: PropTypes.any,
    onClickCallback: PropTypes.func.isRequired,
    basePath: PropTypes.any,
}
